import { error, neutral, petrol, success, yellow } from '../colors';

const createButtonContainedStyles = ({ ownerState, theme }) => ({
  ...(ownerState?.variant === 'contained' && {
    ...(ownerState?.color === 'primary' && {
      backgroundColor: yellow.main,
      color: petrol.main,
      '&:hover': {
        backgroundColor: petrol.main,
        color: yellow.main,
      },
    }),
    ...(ownerState?.color === 'secondary' && {
      backgroundColor: petrol.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: yellow.main,
        color: petrol.main,
      },
    }),
    ...(ownerState?.color === 'success' && {
      backgroundColor: success.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: success.dark,
        color: theme.palette.common.white,
      },
    }),
    ...(ownerState?.color === 'error' && {
      backgroundColor: error.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: error.dark,
        color: theme.palette.common.white,
      },
    }),
  }),
});
const createButtonOutlinedStyles = ({ ownerState, theme }) => ({
  ...(ownerState?.variant === 'outlined' && {
    ...(ownerState?.color === 'primary' && {
      backgroundColor: theme.palette.common.white,
      borderColor: yellow.main,
      color: petrol.main,
      '&:hover': {
        backgroundColor: yellow.main,
        color: petrol.main,
        borderWidth: '1px',
      },
    }),
    ...(ownerState?.color === 'secondary' && {
      backgroundColor: theme.palette.common.white,
      borderColor: petrol.main,
      color: petrol.main,
      '&:hover': {
        backgroundColor: petrol.main,
        color: theme.palette.common.white,
        borderWidth: '1px',
      },
    }),
    ...(ownerState?.color === 'success' && {
      backgroundColor: theme.palette.common.white,
      borderColor: success.main,
      color: success.main,
      '&:hover': {
        backgroundColor: success.main,
        color: theme.palette.common.white,
        borderWidth: '1px',
      },
    }),
    ...(ownerState?.color === 'error' && {
      backgroundColor: theme.palette.common.white,
      borderColor: error.main,
      color: error.main,
      '&:hover': {
        backgroundColor: error.main,
        color: theme.palette.common.white,
        borderWidth: '1px',
      },
    }),
  }),
});

const createButtonSizes = ({ ownerState, theme }) => ({
  ...(ownerState?.size === 'small' && {
    fontSize: theme.typography['small'].fontSize,
    lineHeight: 1.2,
    padding: '8px 12px',
  }),
  ...(ownerState?.size === 'medium' && {
    fontSize: theme.typography['body2'].fontSize,
    padding: '12px 18px',
  }),
  ...(ownerState?.size === 'large' && {
    fontSize: theme.typography['body1'].fontSize,
    padding: '16px 24px',
  }),
});

/**
 * @returns {import('@mui/system').Components}
 */

export const createComponents = (ssrMatchMedia) => ({
  MuiContainer: {
    styleOverrides: {
      maxWidthXxl: {
        maxWidth: '1600px',
      },
    },
  },

  MuiUseMediaQuery: {
    defaultProps: {
      ssrMatchMedia,
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: { border: `1px solid ${neutral[100]}` },
    },
  },

  MuiBottomNavigation: {
    styleOverrides: {
      root: {
        backgroundColor: 'white',
        boxShadow: '0px 0 24px 0 rgba(209, 209, 209, .1)',
        paddingTop: '2.6em',
        paddingBottom: '2.8em',
        zIndex: 2,
        width: '100%',
        position: 'fixed',
        bottom: 0,
      },
    },
  },

  MuiIconButton: {
    variants: [
      {
        props: { variant: 'roundMinimal' },
        style: {
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: petrol.main,
            color: 'white',
          },
        },
      },
    ],
  },

  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        color: yellow,
        '&.Mui-selected': {
          color: petrol,
          fontWeight: 'bold',
          transition: 'color 1s',
        },
        '&.MuiBottomNavigationAction-root': {
          minWidth: '72px',
        },
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        textTransform: 'none',
        fontSize: '16px',
        whiteSpace: 'break-spaces',
        lineHeight: 1,
        '&': {
          borderWidth: '1px',
          textTransform: 'none',
        },
        '&:hover': {
          cursor: 'pointer',
          borderWidth: '1px',
        },
        '&.Mui-disabled': {
          backgroundColor: 'action.disabledBackground',
          color: 'action.disabled',
        },
        ...createButtonSizes({ ownerState, theme }),
        ...createButtonContainedStyles({ ownerState, theme }),
        ...createButtonOutlinedStyles({ ownerState, theme }),
      }),
    },
    variants: [
      {
        props: { size: 'extraLarge' },
        style: { fontSize: 18, padding: '24px 32px' },
      },
    ],
  },

  MuiLoadingButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        lineHeight: 1,
        '&': {
          borderWidth: '2px',
          textTransform: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: 'action.disabledBackground',
          color: 'action.disabled',
        },
        ...createButtonSizes({ ownerState, theme }),
        ...createButtonContainedStyles({ ownerState, theme }),
        ...createButtonOutlinedStyles({ ownerState, theme }),
      }),
    },
  },

  MuiInputBase: {
    styleOverrides: {
      root: {
        margin: 0,
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-input': {
          backgroundColor: 'white',
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: neutral[200],
          },
          '&:hover fieldset': {
            borderColor: petrol.main,
          },
          '&.Mui-focused fieldset': {
            borderColor: yellow.main,
          },
        },
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        backgroundColor: yellow.light,
        color: yellow.main,
        fontWeight: 'bold',
      },
    },
    variants: [
      {
        props: { variant: 'info' },
        style: {
          backgroundColor: 'white',
          padding: 0,
        },
      },
    ],
  },

  MuiTypography: {
    styleOverrides: {
      root: {
        whiteSpace: 'break-spaces',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '0.8rem',
        padding: '8px 16px',
        lineHeight: 1.5,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      icon: {
        display: 'flex',
        alignItems: 'center',
        padding: 0,
      },
      message: {
        padding: 0,
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.info.main,
        textDecoration: 'none',
        '&:hover': {
          color: theme.palette.info.dark,
          textDecoration: 'underline',
          cursor: 'pointer',
        },
      }),
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        '&:last-child': {
          padding: 16,
        },
      },
    },
  },

  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        '&.MuiBackdrop-invisible': {
          backgroundColor: 'transparent', // Ensure invisible backdrop remains transparent
        },
      },
    },
  },

  MuiList: {
    styleOverrides: {
      padding: {
        padding: '10px',
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        '& .MuiSwitch-switchBase': {
          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              backgroundColor: yellow.main,
            },
            '& > .MuiSwitch-thumb': {
              backgroundColor: yellow.main,
            },
          },
          '& > .MuiSwitch-thumb': {
            backgroundColor: petrol.main,
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: petrol.main,
          },
        },
        '& .MuiSwitch-track': {
          backgroundColor: petrol.alpha50,
        },
      },
    },
  },
});
